import axios, { AxiosInstance } from 'axios';

export interface FormStats {
  total: number;
  approved: number;
  flagged: number;
  spam: number;
  pending: number;
  trend: number[];  // 14-day submission counts
}

export interface Form {
  id: number;
  hash: string;
  name: string;
  domain: string;
  description?: string | null;
  allowedOrigins?: string[];
  channelCount: number;
  flaggedChannelCount?: number;
  spamChannelCount?: number;
  endpoint: string;
  createdAt: string;
  channels?: Channel[];
  flaggedChannels?: Channel[];
  spamChannels?: Channel[];
  updatedAt?: string;
  testMode?: boolean;
  spamThreshold?: number | null;
  notificationsEnabled?: boolean;
  spamDetectionDisabled?: boolean;
  responseMode?: 'json' | 'redirect' | 'custom_json';
  responseConfig?: Record<string, any> | null;
  adapterType?: 'flat' | 'event_envelope';
  eventTypePath?: string | null;
  unwrapPath?: string | null;
  hasSigningSecret?: boolean;
  stats?: FormStats;
}

export interface RotateSigningSecretResult {
  signingSecret: string;
  rotatedAt: string;
}

export interface Channel {
  id: number;
  name: string;
  type: 'slack' | 'pushover' | 'email' | 'convertkit' | 'webhook' | 'mautic' | 'twilio-sms' | 'espocrm' | 'pbx-crm';
  config?: Record<string, any>;
  formCount?: number;
  createdAt: string;
  updatedAt?: string;
}

export interface ApiKey {
  id: number;
  name: string;
  description?: string | null;
  role: 'user' | 'admin';
  active: boolean;
  createdAt: string;
  lastUsedAt?: string | null;
  expiresAt?: string | null;
  permissions?: string[];
}

export interface AvailablePermission {
  key: string;
  label: string;
  category: string;
}

export interface ReputationObservation {
  id: number;
  email: string | null;
  email_normalized: string | null;
  ip: string | null;
  asn: number | null;
  context: string | null;
  caller_app: string;
  recommendation: 'allow' | 'shadowban' | 'block';
  score: number;
  reasons: string[];
  created_at: string;
}

export interface KnownBadEmail {
  id: number;
  email_normalized: string;
  first_seen_at: string;
  last_seen_at: string;
  observation_count: number;
  source: 'manual' | 'auto_promoted';
  notes: string | null;
}

export interface ReputationStats {
  observations_24h: number;
  observations_7d: number;
  observations_all_time: number;
  recommendation_distribution_24h: Record<string, number>;
  top_callers_7d: { caller_app: string; count: number }[];
  top_reasons_7d: { reason: string; count: number }[];
  known_bad_emails_total: number;
  known_bad_emails_by_source: Record<string, number>;
}

export interface ObservationsFilters {
  page?: number;
  limit?: number;
  recommendation?: 'allow' | 'shadowban' | 'block';
  caller_app?: string;
  email?: string;
  ip?: string;
  min_score?: number;
  since?: string;
}

export interface KnownBadEmailFilters {
  page?: number;
  limit?: number;
  source?: 'manual' | 'auto_promoted';
  q?: string;
}

export interface CreateFormRequest {
  name: string;
  domain: string;
  allowedOrigins: string[];
  description?: string;
  testMode?: boolean;
  spamThreshold?: number | null;
  notificationsEnabled?: boolean;
  spamDetectionDisabled?: boolean;
  responseMode?: 'json' | 'redirect' | 'custom_json';
  responseConfig?: Record<string, any> | null;
  adapterType?: 'flat' | 'event_envelope';
  eventTypePath?: string | null;
  unwrapPath?: string | null;
}

export interface CreateChannelRequest {
  name: string;
  type: 'slack' | 'pushover' | 'email' | 'convertkit' | 'webhook' | 'mautic' | 'twilio-sms' | 'espocrm' | 'pbx-crm';
  config: Record<string, any>;
}

export type SubmissionStatus = 'pending' | 'approved' | 'flagged' | 'spam';

export interface Submission {
  id: number;
  formHash: string;
  status: SubmissionStatus;
  spamScore: number;
  spamResponse?: string | null;
  submittingIp: string;
  origin: string;
  createdAt: string;
  processedAt?: string | null;
  data: Record<string, any>;
  testMode?: boolean;
}

export interface SubmissionListParams {
  limit?: number;
  page?: number;
  formHash?: string;
  status?: SubmissionStatus;
  includePending?: boolean;
  sortBy?: 'id' | 'status' | 'spamScore' | 'createdAt';
  sortDirection?: 'asc' | 'desc';
}

export interface SubmissionsResponse {
  submissions: Submission[];
  meta: ApiMeta;
}

export type IPListType = 'whitelist' | 'blacklist' | 'neutral';

export interface IPEntry {
  id: number;
  ipAddress: string;
  listType: IPListType;
  reason: string;
  isPermanent: boolean;
  createdAt: string;
  lastSeenAt?: string;
  submissionCount?: number;
  asn?: number | null;
  asnName?: string | null;
  shadowbanned: boolean;
  shadowbannedAt: string | null;
  shadowbanReason: string | null;
}

// Paginated response wrapper (legacy format)
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// New API response format with meta
export interface ApiMeta {
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
  hasMore?: boolean;
  timestamp: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta: ApiMeta;
}

export interface ApiListResponse<T> {
  success: boolean;
  data: T[];
  meta: ApiMeta;
}

export interface ApiError {
  success: false;
  error: string;
  code: 'NOT_FOUND' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'VALIDATION_ERROR' | 'CONFLICT';
}

// IP list query params
export interface IPListParams {
  page?: number;
  limit?: number;
  search?: string;
  listType?: IPListType | '';
  shadowbannedOnly?: boolean;
  sortBy?: 'ipAddress' | 'lastSeenAt' | 'submissionCount' | 'createdAt';
  sortDir?: 'asc' | 'desc';
  asn?: number;
}

// ASN Stats / Reputation
export type ASNDisposition = 'uncharacterized' | 'good' | 'bad';

export interface ASNStats {
  asn: number;
  asnName: string | null;
  uniqueIps: number;
  uniqueShadowbannedIps: number;
  totalSubmissions: number;
  disposition: ASNDisposition;
  shadowbanned: boolean;
  shadowbannedAt: string | null;
  shadowbanReason: string | null;
  banCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface ASNStatsParams {
  page?: number;
  limit?: number;
  search?: string;
  shadowbannedOnly?: boolean;
  sortBy?: 'asn' | 'asnName' | 'uniqueShadowbannedIps' | 'totalSubmissions';
  sortDir?: 'asc' | 'desc';
}

// IP Submissions drill-down
export interface IPSubmissionItem {
  id: number;
  formHash: string;
  status: 'pending' | 'approved' | 'spam' | 'rejected';
  spamScore: number;
  spamCheckResponse: string;
  data: Record<string, any>;
  origin: string;
  userAgent: string;
  country: string;
  createdAt: string;
}

export interface IPSubmissionsData {
  ip: {
    id: number;
    ipAddress: string;
    listType: 'whitelist' | 'blacklist' | 'neutral';
    reason: string | null;
  };
  submissions: IPSubmissionItem[];
}

// ASN Submissions drill-down
export interface ASNSubmissionItem {
  id: number;
  formHash: string;
  submittingIp: string;
  status: string;
  spamScore: number;
  spamCheckResponse: string;
  data: Record<string, any>;
  origin: string;
  createdAt: string;
}

export interface ASNSubmissionsData {
  asn: {
    asn: number;
    name: string | null;
    shadowbanned?: boolean;
    uniqueIps?: number;
  };
  ips: string[];
  submissions: ASNSubmissionItem[];
}

// MaxMind config
export interface MaxMindConfig {
  configured: boolean;
  databaseExists: boolean;
  accountId?: string;
  threshold: number;
}

// Cost Analytics Types
export interface CostPeriodData {
  total_cost_usd: number;
  total_input_tokens: number;
  total_output_tokens: number;
  request_count: number;
  start_date: string;
  end_date: string;
  change_from_previous: number | null;
}

export interface CostPeriodResponse {
  current: CostPeriodData;
  previous: CostPeriodData | null;
  period_label: string;
  current_label: string;
  previous_label: string;
}

export interface CostOverview {
  daily: CostPeriodResponse;
  weekly: CostPeriodResponse;
  monthly: CostPeriodResponse;
  quarterly: CostPeriodResponse;
  annual: CostPeriodResponse;
  all_time: {
    total_cost_usd: number;
    total_input_tokens: number;
    total_output_tokens: number;
    total_requests: number;
  };
}

export interface CostHistoryDay {
  date: string;
  total_cost: string;
  total_input_tokens: string;
  total_output_tokens: string;
  request_count: number;
}

export interface CostHistoryResponse {
  start_date: string;
  end_date: string;
  days: number;
  history: CostHistoryDay[];
}

export interface CostBreakdownItem {
  service?: string;
  model?: string;
  total_cost: string;
  total_input_tokens: string;
  total_output_tokens: string;
  request_count: number;
}

export interface CostBreakdownResponse {
  start_date: string;
  end_date: string;
  breakdown: CostBreakdownItem[];
}

export interface ModelPricing {
  model: string;
  input_per_million: number;
  output_per_million: number;
}

export interface PricingResponse {
  pricing: ModelPricing[];
  note: string;
}

// Permissions
export interface UserPermissions {
  submissions: {
    view: boolean;
    moderate: boolean;
    delete: boolean;
    delete_permanent: boolean;
  };
  ip: {
    view: boolean;
    manage: boolean;
  };
  forms: {
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
  };
  channels: {
    view: boolean;
    manage: boolean;
  };
  integrations: {
    view: boolean;
    manage: boolean;
  };
  analytics: {
    view: boolean;
  };
  costs: {
    view: boolean;
  };
  api_keys: {
    view: boolean;
    manage: boolean;
  };
  settings: {
    view: boolean;
    manage: boolean;
  };
  reputation: {
    view: boolean;
    manage: boolean;
  };
}

// App Status Types
export interface AppStatus {
  operational: boolean;
  providers: {
    ai: { configured: boolean; provider: string | null };
    email: { configured: boolean; provider: string | null };
  };
  counts: {
    forms: number;
    channels: number;
    email_channels: number;
  };
  capabilities: {
    accept_submissions: boolean;
    spam_detection: boolean;
    email_notifications: boolean;
  };
  warnings: string[];
  setup_checklist: SetupChecklistItem[];
}

export interface SetupChecklistItem {
  step: string;
  label: string;
  description?: string;
  completed: boolean;
  required: boolean;
  endpoint: string;
}

// Settings Types
export interface SettingsStatus {
  providers: {
    ai: { configured: boolean; provider: string | null };
    email: { configured: boolean; provider: string | null };
  };
}

export interface AIModel {
  code: string;
  name: string;
  provider: 'anthropic' | 'openai';
}

export interface AIProviderStatus {
  configured: boolean;
}

export interface AISettings {
  active_provider: string | null;
  active_model: string | null;
  providers: {
    anthropic: AIProviderStatus;
    openai: AIProviderStatus;
  };
}

export interface AISettingsUpdate {
  active_model: string;
}

export interface AIProviderConfigUpdate {
  api_key: string;
}

export interface AISettingsResponse {
  success: boolean;
  active_provider: string | null;
  active_model: string | null;
  providers: {
    anthropic: AIProviderStatus;
    openai: AIProviderStatus;
  };
}

export interface EmailSettings {
  provider: string | null;
  region: string | null;
  from_email: string | null;
  configured: boolean;
}

export interface EmailSettingsUpdate {
  access_key: string;
  secret_key: string;
  region: string;
  from_email: string;
}

export interface EmailSettingsResponse {
  success: boolean;
  provider: string;
  region: string;
  from_email: string;
  configured: boolean;
}

export interface SESRegion {
  code: string;
  name: string;
}

// Rate Limiting Types
export interface RateLimitSettings {
  per_ip_per_minute: number;
  per_form_per_minute: number;
  blacklist_threshold: number;
}

// Analytics Types (matching actual API responses)
export interface AnalyticsOverview {
  totals: {
    approved: number;
    flagged: number;
    spam: number;
    blacklisted: number;
    all: number;
  };
  period: {
    days: number;
    submissions: number;
    approvalRate: number;
    spamRate: number;
  };
}

export interface AnalyticsTrendDay {
  date: string;
  total: number;
  approved: number;
  flagged: number;
  spam: number;
}

// Trends endpoint returns array directly
export type AnalyticsTrends = AnalyticsTrendDay[];

export interface AnalyticsFormStats {
  formHash: string;
  total: number;
  approved: number;
  flagged: number;
  spam: number;
  approvalRate: number;
}

// Forms endpoint returns array directly
export type AnalyticsForms = AnalyticsFormStats[];

export interface AnalyticsSpamAccuracy {
  period: {
    days: number;
    start: string;
    end: string;
  };
  detection: {
    totalProcessed: number;
    autoApproved: number;
    autoFlagged: number;
    autoSpam: number;
  };
  accuracy: {
    falsePositives: number;
    falseNegatives: number;
    falsePositiveRate: number;
    falseNegativeRate: number;
  };
  scoreDistribution: {
    low: number;
    medium: number;
    high: number;
  };
}

export interface AnalyticsGeoCountry {
  country: string;
  count: number;
  percentage: number;
}

// Geo endpoint returns array directly
export type AnalyticsGeo = AnalyticsGeoCountry[];

export interface AnalyticsBrowserItem {
  name: string;
  count: number;
  percentage: number;
}

export interface AnalyticsBrowsers {
  browsers: AnalyticsBrowserItem[];
  platforms: AnalyticsBrowserItem[];
  devices: AnalyticsBrowserItem[];
}

export interface UserInfo {
  authenticated: boolean;
  role: 'user' | 'admin';
  role_label: string;
  permissions: UserPermissions;
}

export class PermissionDeniedError extends Error {
  permission: string;
  constructor(message: string, permission: string) {
    super(message);
    this.name = 'PermissionDeniedError';
    this.permission = permission;
  }
}

// Helper to unwrap API responses that may be in new wrapped format or old direct format
function unwrapResponse<T>(response: { data: any }, key?: string): T {
  const data = response.data;

  // New wrapped format: { success: boolean, data: T, meta?: {...} }
  if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
    const innerData = data.data;
    // If a specific key is requested (e.g., 'forms'), extract it
    if (key && innerData && typeof innerData === 'object' && key in innerData) {
      return innerData[key];
    }
    return innerData;
  }

  // Old direct format: T or { [key]: T }
  if (key && data && typeof data === 'object' && key in data) {
    return data[key];
  }

  return data;
}

class ApiClient {
  private client: AxiosInstance;
  private apiKey: string = '';
  private hostname: string = '';

  constructor(apiKey: string = '', hostname: string = getDefaultApiHostname()) {
    this.apiKey = apiKey;
    this.hostname = normalizeApiHostname(hostname);
    const baseURL = `${this.hostname}/api`;
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      if (this.apiKey) {
        config.headers['X-API-Key'] = this.apiKey;
      }
      return config;
    });

    // Handle 403 errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 403) {
          const message = error.response?.data?.message || 'Permission denied';
          const permissionMatch = message.match(/Permission denied: (.+) required/);
          const permission = permissionMatch ? permissionMatch[1] : 'unknown';
          throw new PermissionDeniedError(message, permission);
        }
        throw error;
      }
    );
  }

  // Get current user info and permissions
  async getMe(): Promise<UserInfo> {
    const response = await this.client.get('/me');
    return unwrapResponse<UserInfo>(response);
  }

  setApiKey(apiKey: string) {
    this.apiKey = apiKey;
  }

  // Forms
  async listForms(): Promise<Form[]> {
    const response = await this.client.get('/forms');
    return unwrapResponse<Form[]>(response, 'forms');
  }

  async getForm(id: number): Promise<Form> {
    const response = await this.client.get(`/forms/${id}`);
    return unwrapResponse<Form>(response);
  }

  async createForm(data: CreateFormRequest): Promise<Form> {
    const response = await this.client.post<ApiResponse<Form>>('/forms', data);
    return response.data.data;
  }

  async updateForm(id: number, data: Partial<CreateFormRequest>): Promise<Form> {
    const response = await this.client.put<ApiResponse<Form>>(`/forms/${id}`, data);
    return response.data.data;
  }

  async deleteForm(id: number): Promise<{ deleted: boolean }> {
    const response = await this.client.delete<ApiResponse<{ deleted: boolean }>>(`/forms/${id}`);
    return response.data.data;
  }

  async rotateFormSigningSecret(id: number): Promise<RotateSigningSecretResult> {
    const response = await this.client.post<ApiResponse<RotateSigningSecretResult>>(`/forms/${id}/rotate-signing-secret`);
    return response.data.data;
  }

  // Channels
  async listChannels(): Promise<Channel[]> {
    const response = await this.client.get('/channels');
    return unwrapResponse<Channel[]>(response, 'channels');
  }

  async getChannel(id: number): Promise<Channel> {
    const response = await this.client.get<ApiResponse<Channel>>(`/channels/${id}`);
    return response.data.data;
  }

  async createChannel(data: CreateChannelRequest): Promise<Channel> {
    const response = await this.client.post<ApiResponse<Channel>>('/channels', data);
    return response.data.data;
  }

  async updateChannel(
    id: number,
    data: Partial<CreateChannelRequest>
  ): Promise<Channel> {
    const response = await this.client.put<ApiResponse<Channel>>(`/channels/${id}`, data);
    return response.data.data;
  }

  async deleteChannel(id: number): Promise<{ deleted: boolean }> {
    const response = await this.client.delete<ApiResponse<{ deleted: boolean }>>(
      `/channels/${id}`
    );
    return response.data.data;
  }

  async testChannel(id: number, data?: { email?: string }): Promise<{ success: boolean }> {
    const response = await this.client.post<ApiResponse<{ success: boolean }>>(
      `/channels/${id}/test`,
      data || {}
    );
    return response.data.data;
  }

  // Form-Channel Association
  async attachChannelToForm(
    formId: number,
    channelId: number
  ): Promise<{ channels: Channel[] }> {
    const response = await this.client.post<ApiResponse<{ channels: Channel[] }>>(
      `/forms/${formId}/channels/${channelId}`
    );
    return response.data.data;
  }

  async detachChannelFromForm(
    formId: number,
    channelId: number
  ): Promise<{ channels: Channel[] }> {
    const response = await this.client.delete<ApiResponse<{ channels: Channel[] }>>(
      `/forms/${formId}/channels/${channelId}`
    );
    return response.data.data;
  }

  // Flagged Channel Association (for notifications when submissions need review)
  async attachFlaggedChannelToForm(
    formId: number,
    channelId: number
  ): Promise<{ flaggedChannels: Channel[] }> {
    const response = await this.client.post<ApiResponse<{ flaggedChannels: Channel[] }>>(
      `/forms/${formId}/flagged-channels/${channelId}`
    );
    return response.data.data;
  }

  async detachFlaggedChannelFromForm(
    formId: number,
    channelId: number
  ): Promise<{ flaggedChannels: Channel[] }> {
    const response = await this.client.delete<ApiResponse<{ flaggedChannels: Channel[] }>>(
      `/forms/${formId}/flagged-channels/${channelId}`
    );
    return response.data.data;
  }

  // Spam Channel Association (for notifications when submissions are marked as spam)
  async attachSpamChannelToForm(
    formId: number,
    channelId: number
  ): Promise<{ spamChannels: Channel[] }> {
    const response = await this.client.post<ApiResponse<{ spamChannels: Channel[] }>>(
      `/forms/${formId}/spam-channels/${channelId}`
    );
    return response.data.data;
  }

  async detachSpamChannelFromForm(
    formId: number,
    channelId: number
  ): Promise<{ spamChannels: Channel[] }> {
    const response = await this.client.delete<ApiResponse<{ spamChannels: Channel[] }>>(
      `/forms/${formId}/spam-channels/${channelId}`
    );
    return response.data.data;
  }

  // API Keys
  async listApiKeys(): Promise<ApiKey[]> {
    const response = await this.client.get<ApiListResponse<ApiKey>>('/api-keys');
    return response.data.data;
  }

  async getApiKey(id: number): Promise<ApiKey> {
    const response = await this.client.get<ApiResponse<ApiKey>>(`/api-keys/${id}`);
    return response.data.data;
  }

  async createApiKey(data: {
    name: string;
    description?: string;
    role?: 'user' | 'admin';
    expiresIn?: number;
    permissions?: string[];
  }): Promise<ApiKey & { key: string }> {
    const response = await this.client.post<ApiResponse<ApiKey & { key: string }>>(
      '/api-keys',
      data
    );
    return response.data.data;
  }

  async listAvailablePermissions(): Promise<AvailablePermission[]> {
    const response = await this.client.get<ApiResponse<AvailablePermission[]>>('/api-keys/permissions');
    return response.data.data;
  }

  async deactivateApiKey(id: number): Promise<{ message: string }> {
    const response = await this.client.post<ApiResponse<{ message: string }>>(
      `/api-keys/${id}/deactivate`
    );
    return response.data.data;
  }

  async deleteApiKey(id: number): Promise<{ message: string }> {
    const response = await this.client.delete<ApiResponse<{ message: string }>>(
      `/api-keys/${id}`
    );
    return response.data.data;
  }

  async rotateApiKey(id: number): Promise<ApiKey & { key: string }> {
    const response = await this.client.post<ApiResponse<ApiKey & { key: string }>>(
      `/api-keys/${id}/rotate`
    );
    return response.data.data;
  }

  async checkPermissions(): Promise<{ permissions: string[]; is_admin: boolean }> {
    const response = await this.client.get('/api-keys/me/permissions');
    return unwrapResponse<{ permissions: string[]; is_admin: boolean }>(response);
  }

  // ConvertKit Integration
  async getConvertkitTags(apiKey: string): Promise<Array<{ id: number; name: string }>> {
    const response = await this.client.post<ApiResponse<{ tags: Array<{ id: number; name: string }> }>>(
      '/integrations/convertkit/tags',
      { convertkitApiKey: apiKey }
    );
    return response.data.data.tags;
  }

  async subscribeConvertkit(params: {
    email: string;
    firstName?: string;
    tagId?: number;
    sequenceId?: number;
    convertkitApiKey: string;
  }): Promise<{ subscription: { id: number; state: string; subscriber: { id: number } } }> {
    const response = await this.client.post<ApiResponse<{ subscription: { id: number; state: string; subscriber: { id: number } } }>>(
      '/integrations/convertkit/subscribe',
      params
    );
    return response.data.data;
  }

  async getChannelTags(channelId: number): Promise<Array<{ id: number; name: string }>> {
    const response = await this.client.get<ApiResponse<{ tags: Array<{ id: number; name: string }> }>>(
      `/channels/${channelId}/tags`
    );
    return response.data.data.tags;
  }

  async getConvertkitSequences(apiKey: string): Promise<Array<{ id: number; name: string; type: string }>> {
    const response = await this.client.post<ApiResponse<{ sequences: Array<{ id: number; name: string; type: string }> }>>(
      '/integrations/convertkit/sequences',
      { convertkitApiKey: apiKey }
    );
    return response.data.data.sequences;
  }

  async getConvertkitFormsAndSequences(apiKey: string): Promise<{
    forms: Array<{ id: number; name: string; type: string }>;
    sequences: Array<{ id: number; name: string; type: string }>;
    total: number;
  }> {
    const response = await this.client.post<ApiResponse<{
      forms: Array<{ id: number; name: string; type: string }>;
      sequences: Array<{ id: number; name: string; type: string }>;
      total: number;
    }>>('/integrations/convertkit/forms-and-sequences', { convertkitApiKey: apiKey });
    return response.data.data;
  }

  // ConvertKit Field Mapping
  async getConvertkitFieldMapping(formId: number): Promise<{ fieldMap: Record<string, any> }> {
    const response = await this.client.get<ApiResponse<{ fieldMap: Record<string, any> }>>(
      `/forms/${formId}/convertkit-field-mapping`
    );
    return response.data.data;
  }

  async setConvertkitFieldMapping(formId: number, fieldMap: Record<string, any>): Promise<{ fieldMap: Record<string, any> }> {
    const response = await this.client.post<ApiResponse<{ fieldMap: Record<string, any> }>>(
      `/forms/${formId}/convertkit-field-mapping`,
      { fieldMap }
    );
    return response.data.data;
  }

  // Mautic Integration
  async getMauticSegments(channelId: number): Promise<Array<{ id: number; name: string; alias: string }>> {
    const response = await this.client.get<ApiResponse<{ segments: Array<{ id: number; name: string; alias: string }> }>>(
      `/channels/${channelId}/mautic-segments`
    );
    return response.data.data.segments;
  }

  async getMauticFields(channelId: number): Promise<Array<{ alias: string; label: string; type: string; group: string }>> {
    const response = await this.client.get<ApiResponse<{ fields: Array<{ alias: string; label: string; type: string; group: string }> }>>(
      `/channels/${channelId}/mautic-fields`
    );
    return response.data.data.fields;
  }

  async getMauticTags(channelId: number): Promise<Array<{ id: number; tag: string }>> {
    const response = await this.client.get<ApiResponse<{ tags: Array<{ id: number; tag: string }> }>>(
      `/channels/${channelId}/mautic-tags`
    );
    return response.data.data.tags;
  }

  async getMauticEmails(channelId: number): Promise<Array<{ id: number; name: string; subject: string; emailType: string; isPublished: boolean }>> {
    const response = await this.client.get<ApiResponse<{ emails: Array<{ id: number; name: string; subject: string; emailType: string; isPublished: boolean }> }>>(
      `/channels/${channelId}/mautic-emails`
    );
    return response.data.data.emails;
  }

  async getMauticCampaigns(channelId: number): Promise<Array<{ id: number; name: string; isPublished: boolean; events: Array<{ id: number; name: string; type: string; eventType: string; order: number; channelId: number | null }> }>> {
    const response = await this.client.get<ApiResponse<{ campaigns: Array<{ id: number; name: string; isPublished: boolean; events: Array<{ id: number; name: string; type: string; eventType: string; order: number; channelId: number | null }> }> }>>(
      `/channels/${channelId}/mautic-campaigns`
    );
    return response.data.data.campaigns;
  }

  // Twilio SMS
  async getTwilioPhoneNumbers(channelId: number): Promise<Array<{ sid: string; phoneNumber: string; friendlyName: string; smsCapable: boolean }>> {
    const response = await this.client.get<ApiResponse<{ phoneNumbers: Array<{ sid: string; phoneNumber: string; friendlyName: string; smsCapable: boolean }> }>>(
      `/channels/${channelId}/twilio-phone-numbers`
    );
    return response.data.data.phoneNumbers;
  }

  // EspoCRM
  async getEspoCrmTeams(channelId: number): Promise<Array<{ id: string; name: string }>> {
    const response = await this.client.get<ApiResponse<{ teams: Array<{ id: string; name: string }> }>>(
      `/channels/${channelId}/espocrm-teams`
    );
    return response.data.data.teams;
  }

  async getEspoCrmFields(channelId: number): Promise<Array<{ name: string; label: string; type: string; required: boolean }>> {
    const response = await this.client.get<ApiResponse<{ fields: Array<{ name: string; label: string; type: string; required: boolean }> }>>(
      `/channels/${channelId}/espocrm-fields`
    );
    return response.data.data.fields;
  }

  async getMauticFieldMapping(formId: number): Promise<{ id: number; mauticFieldMap: Record<string, string> | null }> {
    const response = await this.client.get<ApiResponse<{ id: number; mauticFieldMap: Record<string, string> | null }>>(
      `/forms/${formId}/mautic-field-mapping`
    );
    return response.data.data;
  }

  async setMauticFieldMapping(formId: number, fieldMap: Record<string, string>): Promise<{ id: number; mauticFieldMap: Record<string, string> }> {
    const response = await this.client.post<ApiResponse<{ id: number; mauticFieldMap: Record<string, string> }>>(
      `/forms/${formId}/mautic-field-mapping`,
      { fieldMap }
    );
    return response.data.data;
  }

  // Submissions
  async listSubmissions(params: SubmissionListParams = {}): Promise<SubmissionsResponse> {
    const response = await this.client.get<ApiListResponse<Submission>>('/submissions', { params });
    return { submissions: response.data.data, meta: response.data.meta };
  }

  async getSubmission(id: number): Promise<Submission> {
    const response = await this.client.get<ApiResponse<Submission>>(`/submissions/${id}`);
    return response.data.data;
  }

  async deleteSubmission(id: number): Promise<void> {
    await this.client.delete(`/submissions/${id}`);
  }

  async approveSubmission(id: number): Promise<Submission & { notificationsSent?: number }> {
    const response = await this.client.post<ApiResponse<Submission & { notificationsSent?: number }>>(`/submissions/${id}/approve`);
    return response.data.data;
  }

  async flagSubmission(id: number): Promise<Submission> {
    const response = await this.client.post<ApiResponse<Submission>>(`/submissions/${id}/flag`);
    return response.data.data;
  }

  async markSubmissionSpam(id: number): Promise<Submission> {
    const response = await this.client.post<ApiResponse<Submission>>(`/submissions/${id}/mark-spam`);
    return response.data.data;
  }

  // Bulk Submission Actions
  async bulkApproveSubmissions(ids: number[]): Promise<{ approved: number; failed: number; ipsUnblacklisted?: number; notificationsSent?: number }> {
    const response = await this.client.post<ApiResponse<{ approved: number; failed: number; ipsUnblacklisted?: number; notificationsSent?: number }>>(
      '/submissions/bulk/approve',
      { ids }
    );
    return response.data.data;
  }

  async bulkFlagSubmissions(ids: number[]): Promise<{ flagged: number; failed: number }> {
    const response = await this.client.post<ApiResponse<{ flagged: number; failed: number }>>(
      '/submissions/bulk/flag',
      { ids }
    );
    return response.data.data;
  }

  async bulkMarkSubmissionsSpam(ids: number[]): Promise<{ markedSpam: number; failed: number; notificationsSent?: number }> {
    const response = await this.client.post<ApiResponse<{ markedSpam: number; failed: number; notificationsSent?: number }>>(
      '/submissions/bulk/mark-spam',
      { ids }
    );
    return response.data.data;
  }

  async bulkDeleteSubmissions(ids: number[]): Promise<{ deleted: number; failed: number }> {
    const response = await this.client.delete<ApiResponse<{ deleted: number; failed: number }>>(
      '/submissions/bulk/delete',
      { data: { ids } }
    );
    return response.data.data;
  }

  // Export Submissions
  async exportSubmissions(params: {
    format?: 'csv' | 'json';
    formHash?: string;
    status?: SubmissionStatus;
  } = {}): Promise<Blob> {
    const response = await this.client.get('/submissions/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  // IP Management
  async listIPEntries(params?: IPListParams): Promise<PaginatedResponse<IPEntry>> {
    const response = await this.client.get<ApiListResponse<IPEntry>>('/ip', { params });
    return {
      data: response.data.data,
      pagination: {
        page: response.data.meta.page ?? 1,
        limit: response.data.meta.limit ?? 25,
        total: response.data.meta.total ?? response.data.data.length,
        totalPages: response.data.meta.totalPages ?? 1,
      }
    };
  }

  async getIPStats(): Promise<{ whitelisted: number; blacklisted: number }> {
    const response = await this.client.get<ApiResponse<{ whitelisted: number; blacklisted: number }>>('/ip/stats');
    return response.data.data;
  }

  async getASNStats(params?: ASNStatsParams): Promise<PaginatedResponse<ASNStats>> {
    const response = await this.client.get<ApiListResponse<ASNStats>>('/asn', { params });
    return {
      data: response.data.data,
      pagination: {
        page: response.data.meta.page ?? 1,
        limit: response.data.meta.limit ?? 25,
        total: response.data.meta.total ?? response.data.data.length,
        totalPages: response.data.meta.totalPages ?? 1,
      }
    };
  }

  async getASNDetails(asn: number): Promise<ASNStats> {
    const response = await this.client.get<ApiResponse<ASNStats>>(`/asn/${asn}`);
    return response.data.data;
  }

  async shadowbanASN(asn: number, reason?: string): Promise<ASNStats> {
    const response = await this.client.put<ApiResponse<ASNStats>>(`/asn/${asn}/shadowban`, { reason });
    return response.data.data;
  }

  async removeShadowban(asn: number): Promise<ASNStats> {
    const response = await this.client.delete<ApiResponse<ASNStats>>(`/asn/${asn}/shadowban`);
    return response.data.data;
  }

  async getIPSubmissions(ipId: number, limit?: number): Promise<IPSubmissionsData> {
    const params = limit ? { limit } : {};
    const response = await this.client.get<ApiResponse<IPSubmissionsData>>(`/ip/${ipId}/submissions`, { params });
    return response.data.data;
  }

  async getASNSubmissions(asn: number, limit?: number): Promise<ASNSubmissionsData> {
    const params = limit ? { limit } : {};
    const response = await this.client.get<ApiResponse<ASNSubmissionsData>>(`/asn/${asn}/submissions`, { params });
    return response.data.data;
  }

  async whitelistIP(ip: string, reason?: string): Promise<IPEntry> {
    const response = await this.client.post<IPEntry>('/ip/whitelist', { ip, reason });
    return response.data;
  }

  async blacklistIP(ip: string, reason?: string, permanent?: boolean): Promise<IPEntry> {
    const response = await this.client.post<IPEntry>('/ip/blacklist', { ip, reason, permanent });
    return response.data;
  }

  async removeIP(id: number): Promise<{ success: boolean; message?: string }> {
    const response = await this.client.delete<ApiResponse<{ message: string }>>(`/ip/${id}`);
    return { success: true, message: response.data.data.message };
  }

  async backfillASN(): Promise<{ processed: number; updated: number; failed: number }> {
    const response = await this.client.post<ApiResponse<{ processed: number; updated: number; failed: number }>>(
      '/ip/backfill-asn',
      { force: true }
    );
    return response.data.data;
  }

  async backfillCountry(): Promise<{ processed: number; updated: number; skipped: number }> {
    const response = await this.client.post<ApiResponse<{ processed: number; updated: number; skipped: number }>>(
      '/submissions/backfill-country',
      { force: true }
    );
    return response.data.data;
  }

  async backfillIps(): Promise<{ ipsCreated: number; asnsCreated: number; asnLookupsPerformed: number }> {
    const response = await this.client.post<ApiResponse<{ ipsCreated: number; asnsCreated: number; asnLookupsPerformed: number }>>(
      '/stats/backfill-ips'
    );
    return response.data.data;
  }

  async refreshStats(): Promise<{ ipsUpdated: number; asnsCreated: number; asnsUpdated: number }> {
    const response = await this.client.post<ApiResponse<{ ipsUpdated: number; asnsCreated: number; asnsUpdated: number }>>(
      '/stats/refresh'
    );
    return response.data.data;
  }

  // MaxMind Configuration
  async getMaxMindConfig(): Promise<MaxMindConfig> {
    const response = await this.client.get<ApiResponse<{
      configured: boolean;
      database_exists: boolean;
      account_id?: string;
      asn_blacklist_threshold: number;
    }>>('/settings/maxmind');

    const data = response.data.data;
    return {
      configured: data.configured,
      databaseExists: data.database_exists,
      accountId: data.account_id,
      threshold: data.asn_blacklist_threshold,
    };
  }

  async updateMaxMindConfig(
    accountId: string,
    licenseKey: string,
    threshold: number
  ): Promise<{ success: boolean; configured: boolean; threshold: number }> {
    const response = await this.client.put<ApiResponse<{
      configured: boolean;
      account_id: string;
      asn_blacklist_threshold: number;
    }>>(
      '/settings/maxmind',
      { account_id: accountId, license_key: licenseKey, asn_blacklist_threshold: threshold }
    );

    return {
      success: true,
      configured: response.data.data.configured,
      threshold: response.data.data.asn_blacklist_threshold,
    };
  }

  async deleteMaxMindConfig(): Promise<{ success: boolean; message?: string }> {
    const response = await this.client.delete<ApiResponse<{ message: string }>>('/settings/maxmind');
    return { success: true, message: response.data.data.message };
  }

  async updateMaxMindDatabase(): Promise<{ success: boolean; message: string; databases?: Record<string, any> }> {
    const response = await this.client.post<ApiResponse<{
      message: string;
      databases?: Record<string, any>;
    }>>('/settings/maxmind/update-database');

    return {
      success: true,
      message: response.data.data.message,
      databases: response.data.data.databases,
    };
  }

  // Cost Analytics
  async getCostOverview(): Promise<CostOverview> {
    const response = await this.client.get<ApiResponse<CostOverview>>('/costs');
    return response.data.data;
  }

  async getCostPeriod(period: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual'): Promise<CostPeriodResponse> {
    const response = await this.client.get<ApiResponse<CostPeriodResponse>>(`/costs/${period}`);
    return response.data.data;
  }

  async getCostAllTime(): Promise<CostOverview['all_time']> {
    const response = await this.client.get<ApiResponse<CostOverview['all_time']>>('/costs/all-time');
    return response.data.data;
  }

  async getCostHistory(days: number = 30): Promise<CostHistoryResponse> {
    const response = await this.client.get<ApiResponse<CostHistoryResponse>>('/costs/history', {
      params: { days },
    });
    return response.data.data;
  }

  async getCostByService(startDate?: string, endDate?: string): Promise<CostBreakdownResponse> {
    const params: Record<string, string> = {};
    if (startDate) params.start = startDate;
    if (endDate) params.end = endDate;
    const response = await this.client.get<ApiResponse<CostBreakdownResponse>>('/costs/by-service', { params });
    return response.data.data;
  }

  async getCostByModel(startDate?: string, endDate?: string): Promise<CostBreakdownResponse> {
    const params: Record<string, string> = {};
    if (startDate) params.start = startDate;
    if (endDate) params.end = endDate;
    const response = await this.client.get<ApiResponse<CostBreakdownResponse>>('/costs/by-model', { params });
    return response.data.data;
  }

  async getCostPricing(): Promise<PricingResponse> {
    const response = await this.client.get<ApiResponse<PricingResponse>>('/costs/pricing');
    return response.data.data;
  }

  // App Status
  async getStatus(): Promise<AppStatus> {
    const response = await this.client.get('/status');
    return unwrapResponse<AppStatus>(response);
  }

  // Settings
  async getSettings(): Promise<SettingsStatus> {
    const response = await this.client.get<SettingsStatus>('/settings');
    return response.data;
  }

  async getAISettings(): Promise<AISettings> {
    const response = await this.client.get<ApiResponse<AISettings>>('/settings/ai');
    return response.data.data;
  }

  async getAIModels(provider?: string): Promise<AIModel[]> {
    const params = provider ? { provider } : {};
    const response = await this.client.get<ApiResponse<AIModel[]>>('/settings/ai/models', { params });
    return response.data.data;
  }

  async updateAISettings(data: AISettingsUpdate): Promise<AISettingsResponse> {
    const response = await this.client.put<ApiResponse<AISettingsResponse>>('/settings/ai', data);
    return response.data.data;
  }

  async configureAIProvider(provider: string, apiKey: string): Promise<{ provider: string; configured: boolean }> {
    const response = await this.client.put<ApiResponse<{ provider: string; configured: boolean }>>(
      `/settings/ai/providers/${provider}`,
      { api_key: apiKey }
    );
    return response.data.data;
  }

  async removeAIProvider(provider: string): Promise<{ message: string }> {
    const response = await this.client.delete<ApiResponse<{ message: string }>>(
      `/settings/ai/providers/${provider}`
    );
    return response.data.data;
  }

  async getEmailSettings(): Promise<EmailSettings> {
    const response = await this.client.get<ApiResponse<EmailSettings>>('/settings/email');
    return response.data.data;
  }

  async updateEmailSettings(data: EmailSettingsUpdate): Promise<EmailSettingsResponse> {
    const response = await this.client.put<ApiResponse<EmailSettingsResponse>>('/settings/email', data);
    return response.data.data;
  }

  async getEmailRegions(): Promise<SESRegion[]> {
    const response = await this.client.get<ApiResponse<{ regions: SESRegion[] }>>('/settings/email/regions');
    return response.data.data.regions;
  }

  // Rate Limiting
  async getRateLimitSettings(): Promise<RateLimitSettings> {
    const response = await this.client.get<ApiResponse<RateLimitSettings>>('/settings/rate-limit');
    return response.data.data;
  }

  async updateRateLimitSettings(data: Partial<RateLimitSettings>): Promise<RateLimitSettings> {
    const response = await this.client.put<ApiResponse<RateLimitSettings>>('/settings/rate-limit', data);
    return response.data.data;
  }

  // Analytics
  async getAnalyticsOverview(days: number = 30): Promise<AnalyticsOverview> {
    const response = await this.client.get<ApiResponse<AnalyticsOverview>>('/analytics/overview', {
      params: { days },
    });
    return response.data.data;
  }

  async getAnalyticsTrends(days: number = 30): Promise<AnalyticsTrends> {
    const response = await this.client.get<ApiResponse<{ trends: AnalyticsTrends }>>('/analytics/trends', {
      params: { days },
    });
    return response.data.data.trends;
  }

  async getAnalyticsForms(days: number = 30): Promise<AnalyticsForms> {
    const response = await this.client.get<ApiResponse<{ forms: AnalyticsForms }>>('/analytics/forms', {
      params: { days },
    });
    return response.data.data.forms;
  }

  async getAnalyticsSpamAccuracy(days: number = 30): Promise<AnalyticsSpamAccuracy> {
    const response = await this.client.get<ApiResponse<AnalyticsSpamAccuracy>>('/analytics/spam-accuracy', {
      params: { days },
    });
    return response.data.data;
  }

  async getAnalyticsGeo(days: number = 30): Promise<AnalyticsGeo> {
    const response = await this.client.get<ApiResponse<{ countries: AnalyticsGeo }>>('/analytics/geo', {
      params: { days },
    });
    return response.data.data.countries;
  }

  async getAnalyticsBrowsers(days: number = 30): Promise<AnalyticsBrowsers> {
    const response = await this.client.get<ApiResponse<AnalyticsBrowsers>>('/analytics/browsers', {
      params: { days },
    });
    return response.data.data;
  }

  // Reputation admin
  async listReputationObservations(filters: ObservationsFilters = {}): Promise<{
    data: ReputationObservation[];
    meta: { total: number; page: number; limit: number; totalPages: number; hasMore: boolean };
  }> {
    const response = await this.client.get<ApiResponse<ReputationObservation[]>>('/reputation/observations', {
      params: filters,
    });
    return { data: response.data.data, meta: response.data.meta as any };
  }

  async listKnownBadEmails(filters: KnownBadEmailFilters = {}): Promise<{
    data: KnownBadEmail[];
    meta: { total: number; page: number; limit: number; totalPages: number; hasMore: boolean };
  }> {
    const response = await this.client.get<ApiResponse<KnownBadEmail[]>>('/reputation/known-bad-emails', {
      params: filters,
    });
    return { data: response.data.data, meta: response.data.meta as any };
  }

  async addKnownBadEmail(email: string, notes?: string): Promise<KnownBadEmail> {
    const response = await this.client.post<ApiResponse<KnownBadEmail>>('/reputation/known-bad-emails', {
      email,
      notes,
    });
    return response.data.data;
  }

  async deleteKnownBadEmail(id: number): Promise<{ message: string }> {
    const response = await this.client.delete<ApiResponse<{ message: string }>>(`/reputation/known-bad-emails/${id}`);
    return response.data.data;
  }

  async getReputationStats(): Promise<ReputationStats> {
    const response = await this.client.get<ApiResponse<ReputationStats>>('/reputation/stats');
    return response.data.data;
  }
}

export function getDefaultApiHostname(): string {
  return process.env.REACT_APP_DEFAULT_API_HOST || 'http://localhost:8000';
}

export function normalizeApiHostname(hostname: string): string {
  const trimmed = hostname.trim();

  if (!trimmed) {
    return getDefaultApiHostname();
  }

  const isLocalHost = /^(localhost|127\.0\.0\.1)(:\d+)?(\/.*)?$/i.test(trimmed);
  const defaultProtocol = isLocalHost ? 'http' : 'https';
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `${defaultProtocol}://${trimmed}`;
  const withoutTrailingSlash = withProtocol.replace(/\/+$/, '');

  return withoutTrailingSlash.replace(/\/api$/i, '');
}

export default ApiClient;
